import { useState } from "react";
import { Link } from "react-router";
import { Trans, useLingui } from "@lingui/macro";
import { ChevronLeft, ChevronRight, Database, Lock } from "lucide-react";
import { cn } from "@app/lib/utils";
import type { DatasetPurpose, DatasetState } from "@app/utils/reference-dataset";

export interface PatientDataset {
  id: string;
  name: string;
  purpose?: DatasetPurpose;
  state?: DatasetState;
}

/** The datasets a patient belongs to, one badge at a time — the chevrons step through the rest. */
export default function PatientDatasetBadges({
  datasets,
}: {
  datasets: PatientDataset[];
}) {
  const { t } = useLingui();
  const [index, setIndex] = useState(0);
  if (datasets.length === 0) return null;

  // The list can shrink under the index (a dataset removed elsewhere): wrap rather than show nothing.
  const position = index % datasets.length;
  const dataset = datasets[position];
  const step = (delta: number) =>
    setIndex((position + delta + datasets.length) % datasets.length);
  const stepping = datasets.length > 1;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="whitespace-nowrap text-xs font-medium text-slate-500">
        <Trans>Jeux de données</Trans>
      </span>
      {dataset.purpose === "reference" ? (
        // Not a link: a sealed reference's page shows nothing of this dossier, and the badge is a
        // warning to whoever tunes the reading, not a way in (PT-155).
        <span
          tabIndex={0}
          title={t`Ce dossier sert de référence au laboratoire : ne pas s'en servir pour régler la lecture.`}
          className={cn(
            "inline-flex min-w-0 items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700",
            stepping ? "w-56" : "max-w-56",
          )}
        >
          <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">
            <Trans>Référence · {dataset.name}</Trans>
          </span>
        </span>
      ) : (
        <Link
          to={`/datasets/${dataset.id}`}
          title={dataset.name}
          className={cn(
            "inline-flex min-w-0 items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/20",
            // A fixed width while stepping, so the chevrons stay under the cursor whatever the name.
            stepping ? "w-56" : "max-w-56",
          )}
        >
          <Database className="h-3 w-3 shrink-0" />
          <span className="truncate">{dataset.name}</span>
        </Link>
      )}
      {stepping && (
        <div className="flex shrink-0 items-center text-xs text-slate-500">
          <button
            type="button"
            onClick={() => step(-1)}
            aria-label={t`Jeu de données précédent`}
            className="rounded-full p-1 transition-colors hover:bg-primary/10 hover:text-primary"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          {/* DM Sans as served ignores tabular-nums and its "0" is its widest digit: reserve the
              counter's width in ch so stepping from 1/3 to 2/3 moves nothing in the header. */}
          <span
            className="text-center"
            style={{ minWidth: `${2 * String(datasets.length).length + 1}ch` }}
            aria-live="polite"
          >
            {position + 1}/{datasets.length}
          </span>
          <button
            type="button"
            onClick={() => step(1)}
            aria-label={t`Jeu de données suivant`}
            className="rounded-full p-1 transition-colors hover:bg-primary/10 hover:text-primary"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
